import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { ScannedDevices } from "../interfaces/ScannedDevices";
import { encryptedRowId } from "./intrusionRowIdentity";
import { mergeEncryptedRows } from "./mergeEncryptedRows";

/**
 * Push the recent-encrypted read into the intrusion log's own list caches.
 *
 * The SSE bus only says "session <id> moved"; the row itself still comes from
 * `recentEncrypted`, so read-side grouping and distance filtering stay in one
 * place. That one read then has to reach the lists the log actually renders —
 * today's and the all-time history — without refetching either of them.
 *
 * Three rules this must not break:
 *
 *  - `incoming` is the WHOLE recent window, never a filtered subset. The merge
 *    reads "cached live row missing from incoming" as "that session ended", so
 *    a partial list would silently kill live rows.
 *  - Only exact keys, never a prefix match: a neighbouring org's or a
 *    neighbouring range filter's cache holds different rows entirely.
 *  - `dataUpdatedAt` is preserved. It is what each list's own focus-refetch
 *    cadence hangs off; a live session ticking once a second would otherwise
 *    keep the today list looking freshly read all day and it would stop
 *    re-reading the day.
 */
export function applyRecentEncryptedToCaches(
  queryClient: QueryClient,
  keys: readonly QueryKey[],
  incoming: readonly ScannedDevices[],
): void {
  for (const key of keys) {
    const state = queryClient.getQueryState<ScannedDevices[]>(key);
    const cached = state?.data;
    // Not fetched yet: let the list's own query seed it. A 30-minute window
    // written here would masquerade as the whole day / whole history.
    if (!cached) continue;
    const next = mergeEncryptedRows(cached, incoming);
    if (!next || next === cached) continue;
    queryClient.setQueryData(key, next, { updatedAt: state.dataUpdatedAt });
  }
}

/**
 * Mark one encrypted session ended, now.
 *
 * A `closed` event is the one thing the bus knows that the next
 * `recentEncrypted` read cannot tell us any sooner, and it is what drops the
 * live badge. Applied independently of the coalesced refetch so the badge goes
 * out on the event, not up to a second later.
 */
export function markEncryptedSessionClosed(
  queryClient: QueryClient,
  keys: readonly QueryKey[],
  id: number,
): void {
  for (const key of keys) {
    const state = queryClient.getQueryState<ScannedDevices[]>(key);
    const cached = state?.data;
    if (!cached) continue;
    let changed = false;
    const next = cached.map((row) => {
      if (row.live !== true || encryptedRowId(row) !== id) return row;
      changed = true;
      return { ...row, live: false };
    });
    if (!changed) continue;
    queryClient.setQueryData(key, next, { updatedAt: state.dataUpdatedAt });
  }
}

/**
 * Has any of these lists actually been fetched?
 *
 * Nothing mounts the encrypted lists until the user opens the intrusion log
 * (or a session dialog, which mounts them itself), so on every other page a
 * live session would otherwise cost one `recentEncrypted` round-trip per
 * second whose result has nowhere to go — `applyRecentEncryptedToCaches`
 * deliberately refuses to seed a cache. An empty list still counts as fetched:
 * an empty day is a real answer, and a live session has to be able to appear
 * in it.
 */
export function hasCachedEncryptedList(
  queryClient: QueryClient,
  keys: readonly QueryKey[],
): boolean {
  return keys.some(
    (key) => queryClient.getQueryState<ScannedDevices[]>(key)?.data !== undefined,
  );
}
